/**
 * The surface dark-mode log rows tint player names against: the turn-start
 * row (`--color-paper-up`), the lightest surface a log row sits on. Plain rows
 * use the darker canvas and the game-end row is darker still, so a name that
 * clears the floor here clears it everywhere in the log. The combat card is
 * lighter again (#303923) and tints against itself; using that here pushed
 * every log name much further toward white than the log needed.
 */
export const DARK_LOG_NAME_SURFACE = '#232a18'

/**
 * Contrast floor for dark-log names. WCAG AA is 4.5:1; the log asks for a
 * little more so the saturated colours that already pass (Bloom, Forge) still
 * get a lift and read as light as their lightened neighbours.
 */
export const DARK_LOG_NAME_CONTRAST = 5

/** The light log surface (`--color-paper`), for the light-mode overrides below. */
export const LIGHT_LOG_NAME_SURFACE = '#f4ecd3'

/**
 * Hand-tuned log name colours per palette colour (keyed by the board hex),
 * reviewed by eye on both themes. Anything not listed falls back to the
 * generic rule: dark mode lightens along the hue to DARK_LOG_NAME_CONTRAST,
 * light mode uses the board colour as is.
 *
 *  - Cinder (dark): the generic lift lands on coral; this is a fuller red.
 *  - Forge, Tide, Bloom (dark): a touch lighter than the generic rule gives.
 *  - Forge, Harvest, Tide (light): the board colour is pale on cream; a shade darker.
 */
export const LOG_NAME_TINT: Record<string, { dark?: string; light?: string }> = {
  '#c12c15': { dark: '#ff6552' },
  '#8b7623': { light: '#7a661d' },
  '#f59e0b': { dark: '#ffb224', light: '#db8c08' },
  '#20c4b0': { dark: '#4fe3d0', light: '#17a593' },
  '#e04fa8': { dark: '#ea7dc0' },
}

/** The colour a player's name renders in a log row for the given theme. */
export function logNameColor(boardHex: string, theme: 'light' | 'dark'): string {
  const tuned = LOG_NAME_TINT[boardHex.toLowerCase()]?.[theme]
  if (tuned) return tuned
  return theme === 'dark' ? readablePlayerTextColor(boardHex, DARK_LOG_NAME_SURFACE, DARK_LOG_NAME_CONTRAST) : boardHex
}

type Rgb = [number, number, number]
type Hsl = [number, number, number]

function parseHex(hex: string): Rgb {
  const digits = hex.slice(1)
  const full = digits.length === 3 ? [...digits].map(c => c + c).join('') : digits
  return [0, 2, 4].map(i => parseInt(full.slice(i, i + 2), 16)) as Rgb
}

function toHex(rgb: Rgb): string {
  return '#' + rgb.map(c => Math.round(Math.min(255, Math.max(0, c))).toString(16).padStart(2, '0')).join('')
}

function toHsl([r, g, b]: Rgb): Hsl {
  r /= 255; g /= 255; b /= 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4
  return [h / 6, s, l]
}

function fromHsl([h, s, l]: Hsl): Rgb {
  if (s === 0) return [l * 255, l * 255, l * 255]
  const channel = (p: number, q: number, t: number) => {
    t = (t + 1) % 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  return [channel(p, q, h + 1 / 3), channel(p, q, h), channel(p, q, h - 1 / 3)].map(v => v * 255) as Rgb
}

function luminance(rgb: Rgb): number {
  return rgb.reduce((sum, value, i) => {
    const c = value / 255
    return sum + (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4) * [0.2126, 0.7152, 0.0722][i]!
  }, 0)
}

function contrastRatio(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)
}

/**
 * Preserve the player's hue while making small text readable on its surface.
 *
 * Lightening moves along HSL lightness with the hue and saturation held, so a
 * red stays red and a blue stays blue instead of washing out to salmon or
 * periwinkle the way mixing toward white did. Darkening (light surfaces)
 * does the same in the other direction. Already-readable colours return
 * unchanged.
 */
export function readablePlayerTextColor(color: string, background: string, minContrast = 4.5): string {
  const base = parseHex(color)
  const surface = luminance(parseHex(background))
  const contrast = (rgb: Rgb) => contrastRatio(luminance(rgb), surface)
  if (contrast(base) >= minContrast) return color
  const [h, s, l] = toHsl(base)
  for (let step = 1; step <= 100; step++) {
    for (const target of [1, 0]) {
      // Measure the rounded channels: that is the colour that actually renders.
      const candidate = fromHsl([h, s, l + (target - l) * step / 100]).map(Math.round) as Rgb
      if (contrast(candidate) >= minContrast) return toHex(candidate)
    }
  }
  return surface > 0.179 ? '#000000' : '#ffffff'
}
